/**
 * TeamSystem Impact - Stakeholder Manager
 * Component for managing external stakeholders
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Rating,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../../api/client';
import { useAuthStore } from '../../../store/authStore';
import { isReadOnlyRole } from '../../../utils/permissions';

// Stakeholder types
interface Stakeholder {
  _id: string;
  name: string;
  category: string;
  description?: string;
  contactInfo?: {
    email?: string;
    phone?: string;
    address?: string;
  };
  influenceLevel: number;
  dependencyLevel: number;
  priorityScore: number;
  engagementFrequency: string;
  active: boolean;
}

interface StakeholderFormData {
  name: string;
  category: string;
  description: string;
  email: string;
  phone: string;
  address: string;
  influenceLevel: number;
  dependencyLevel: number;
}

// Stakeholder categories
const STAKEHOLDER_CATEGORIES = [
  { id: 'dipendenti', label: { it: 'Dipendenti', en: 'Employees' } },
  { id: 'investitori', label: { it: 'Investitori', en: 'Investors' } },
  { id: 'clienti', label: { it: 'Clienti', en: 'Clients' } },
  { id: 'fornitori', label: { it: 'Fornitori', en: 'Suppliers' } },
  { id: 'comunita_locali', label: { it: 'Comunità Locali', en: 'Local Communities' } },
  { id: 'regolatori', label: { it: 'Regolatori', en: 'Regulators' } },
  { id: 'ong', label: { it: 'ONG', en: 'NGOs' } },
  { id: 'sindacati', label: { it: 'Sindacati', en: 'Trade Unions' } },
  { id: 'altro', label: { it: 'Altro', en: 'Other' } },
];

// I18n labels
function useLabels() {
  const { t } = useTranslation();

  return {
    externalStakeholders: t('dma.stakeholderManager.externalStakeholders'),
    addStakeholder: t('dma.stakeholderManager.addStakeholder'),
    editStakeholder: t('dma.stakeholderManager.editStakeholder'),
    name: t('dma.stakeholderManager.name'),
    category: t('dma.stakeholderManager.category'),
    description: t('dma.stakeholderManager.description'),
    email: 'Email',
    phone: t('dma.stakeholderManager.phone'),
    address: t('dma.stakeholderManager.address'),
    influenceLevel: t('dma.stakeholderManager.influenceLevel'),
    dependencyLevel: t('dma.stakeholderManager.dependencyLevel'),
    priorityScore: t('dma.stakeholderManager.priorityScore'),
    actions: t('dma.stakeholderManager.actions'),
    save: t('dma.stakeholderManager.save'),
    cancel: t('dma.stakeholderManager.cancel'),
    delete: t('dma.stakeholderManager.delete'),
    confirmDelete: t('dma.stakeholderManager.confirmDelete'),
    noStakeholders: t('dma.stakeholderManager.noStakeholders'),
    filterByCategory: t('dma.stakeholderManager.filterByCategory'),
    allCategories: t('dma.stakeholderManager.allCategories'),
  };
}

const INITIAL_FORM: StakeholderFormData = {
  name: '',
  category: 'altro',
  description: '',
  email: '',
  phone: '',
  address: '',
  influenceLevel: 3,
  dependencyLevel: 3,
};

export function StakeholderManager() {
  const labels = useLabels();
  const utente = useAuthStore((s) => s.utente);
  const isReadOnly = isReadOnlyRole(utente?.ruolo);
  const { i18n } = useTranslation();
  const lang = i18n.language as 'it' | 'en';
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStakeholder, setEditingStakeholder] = useState<Stakeholder | null>(null);
  const [formData, setFormData] = useState<StakeholderFormData>(INITIAL_FORM);
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [stakeholderToDelete, setStakeholderToDelete] = useState<Stakeholder | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadStakeholders();
  }, []);

  const loadStakeholders = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/materiality/stakeholders');
      setStakeholders(response.data.stakeholders || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (stakeholder?: Stakeholder) => {
    if (stakeholder) {
      setEditingStakeholder(stakeholder);
      setFormData({
        name: stakeholder.name,
        category: stakeholder.category,
        description: stakeholder.description || '',
        email: stakeholder.contactInfo?.email || '',
        phone: stakeholder.contactInfo?.phone || '',
        address: stakeholder.contactInfo?.address || '',
        influenceLevel: stakeholder.influenceLevel,
        dependencyLevel: stakeholder.dependencyLevel,
      });
    } else {
      setEditingStakeholder(null);
      setFormData(INITIAL_FORM);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingStakeholder(null);
    setFormData(INITIAL_FORM);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const data = {
        name: formData.name,
        category: formData.category,
        description: formData.description || undefined,
        contactInfo: {
          email: formData.email || undefined,
          phone: formData.phone || undefined,
          address: formData.address || undefined,
        },
        influenceLevel: formData.influenceLevel,
        dependencyLevel: formData.dependencyLevel,
      };

      if (editingStakeholder) {
        await apiClient.put(`/materiality/stakeholders/${editingStakeholder._id}`, data);
      } else {
        await apiClient.post('/materiality/stakeholders', data);
      }

      handleCloseDialog();
      await loadStakeholders();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!stakeholderToDelete) return;

    try {
      setDeleting(true);
      await apiClient.delete(`/materiality/stakeholders/${stakeholderToDelete._id}`);
      setDeleteDialogOpen(false);
      setStakeholderToDelete(null);
      await loadStakeholders();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const filteredStakeholders = categoryFilter
    ? stakeholders.filter(s => s.category === categoryFilter)
    : stakeholders;

  const getCategoryLabel = (categoryId: string) => {
    const cat = STAKEHOLDER_CATEGORIES.find(c => c.id === categoryId);
    return cat ? cat.label[lang] || cat.label.en : categoryId;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{labels.filterByCategory}</InputLabel>
          <Select
            value={categoryFilter}
            label={labels.filterByCategory}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="">{labels.allCategories}</MenuItem>
            {STAKEHOLDER_CATEGORIES.map(cat => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.label[lang] || cat.label.en}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {!isReadOnly && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            {labels.addStakeholder}
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{labels.name}</TableCell>
              <TableCell>{labels.category}</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="center">{labels.influenceLevel}</TableCell>
              <TableCell align="center">{labels.dependencyLevel}</TableCell>
              <TableCell align="center">{labels.priorityScore}</TableCell>
              <TableCell align="center">{labels.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStakeholders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">{labels.noStakeholders}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredStakeholders.map(stakeholder => (
                <TableRow key={stakeholder._id} hover>
                  <TableCell>
                    <Typography fontWeight={500}>{stakeholder.name}</Typography>
                    {stakeholder.description && (
                      <Typography variant="caption" color="text.secondary">
                        {stakeholder.description.substring(0, 50)}...
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={getCategoryLabel(stakeholder.category)} size="small" />
                  </TableCell>
                  <TableCell>
                    {stakeholder.contactInfo?.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2">{stakeholder.contactInfo.email}</Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Rating value={stakeholder.influenceLevel} readOnly size="small" max={5} />
                  </TableCell>
                  <TableCell align="center">
                    <Rating value={stakeholder.dependencyLevel} readOnly size="small" max={5} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={stakeholder.priorityScore}
                      color={stakeholder.priorityScore >= 15 ? 'error' : stakeholder.priorityScore >= 9 ? 'warning' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {!isReadOnly && (
                      <Tooltip title={labels.editStakeholder}>
                        <IconButton size="small" aria-label={labels.editStakeholder} onClick={() => handleOpenDialog(stakeholder)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!isReadOnly && (
                      <Tooltip title={labels.delete}>
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={labels.delete}
                          onClick={() => {
                            setStakeholderToDelete(stakeholder);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingStakeholder ? labels.editStakeholder : labels.addStakeholder}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={labels.name}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />

            <FormControl fullWidth>
              <InputLabel>{labels.category}</InputLabel>
              <Select
                value={formData.category}
                label={labels.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {STAKEHOLDER_CATEGORIES.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.label[lang] || cat.label.en}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={labels.description}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={labels.email}
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={labels.phone}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>

            <TextField
              label={labels.address}
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              fullWidth
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" gutterBottom>
                  {labels.influenceLevel}
                </Typography>
                <Rating
                  value={formData.influenceLevel}
                  onChange={(_, value) => setFormData({ ...formData, influenceLevel: value || 1 })}
                  max={5}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" gutterBottom>
                  {labels.dependencyLevel}
                </Typography>
                <Rating
                  value={formData.dependencyLevel}
                  onChange={(_, value) => setFormData({ ...formData, dependencyLevel: value || 1 })}
                  max={5}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{labels.cancel}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.name}
          >
            {saving ? <CircularProgress size={20} /> : labels.save}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{labels.delete}</DialogTitle>
        <DialogContent>
          <Typography>{labels.confirmDelete}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{labels.cancel}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : labels.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
